import { useEffect, useState } from "react";
import { supabase } from "../lib/supabase.js";
import { formatAuPhoneForDisplay } from "../lib/phone.js";
import { formatDateTime, formatTimeWithSeconds } from "../lib/datetime.js";

interface BusinessRow {
  id: string;
  name: string;
}

interface CallRow {
  id: string;
  caller_number: string | null;
  started_at: string | null;
  duration_seconds: number | null;
  outcome: string | null;
  summary_status: string;
}

interface CallEventRow {
  id: number;
  at: string;
  type: string;
  detail: unknown;
}

interface CallRawRow {
  transcript: unknown;
  tool_trace: unknown;
  model_messages: unknown;
  usage: unknown;
  latency: unknown;
}

/**
 * Admin-only view over calls/call_raw/call_events -- exactly the tables with NO client RLS
 * policy (supabase/migrations/0001_init.sql section 7). This is deliberately the one screen
 * in the whole app that shows model/prompt internals; docs section 7 point 2 is what keeps
 * that off the client portal's Calls/CallDetail pages.
 */
export function CallDebug() {
  const [businesses, setBusinesses] = useState<BusinessRow[]>([]);
  const [businessId, setBusinessId] = useState("");
  const [calls, setCalls] = useState<CallRow[]>([]);
  const [selectedCallId, setSelectedCallId] = useState<string | null>(null);
  const [raw, setRaw] = useState<CallRawRow | null>(null);
  const [events, setEvents] = useState<CallEventRow[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    supabase
      .from("businesses")
      .select("id, name")
      .order("name")
      .then(({ data }) => {
        const list = (data ?? []) as BusinessRow[];
        setBusinesses(list);
        if (list[0]) setBusinessId(list[0].id);
      });
  }, []);

  useEffect(() => {
    if (!businessId) return;
    setLoading(true);
    setSelectedCallId(null);
    supabase
      .from("calls")
      .select("id, caller_number, started_at, duration_seconds, outcome, summary_status")
      .eq("business_id", businessId)
      .order("started_at", { ascending: false })
      .limit(50)
      .then(({ data }) => {
        setCalls((data ?? []) as CallRow[]);
        setLoading(false);
      });
  }, [businessId]);

  useEffect(() => {
    if (!selectedCallId) {
      setRaw(null);
      setEvents([]);
      return;
    }
    supabase.from("call_raw").select("transcript, tool_trace, model_messages, usage, latency").eq("call_id", selectedCallId).maybeSingle().then(({ data }) => {
      setRaw(data as CallRawRow | null);
    });
    supabase
      .from("call_events")
      .select("id, at, type, detail")
      .eq("call_id", selectedCallId)
      .order("at")
      .then(({ data }) => setEvents((data ?? []) as CallEventRow[]));
  }, [selectedCallId]);

  return (
    <div style={{ display: "flex", gap: 24 }}>
      <div style={{ width: 360, flexShrink: 0 }}>
        <h1 className="rc-h1" style={{ marginBottom: 16 }}>Call debug</h1>
        <label style={{ display: "block", marginBottom: 16, fontSize: 14 }}>
          Business
          <select value={businessId} onChange={(e) => setBusinessId(e.target.value)} style={{ display: "block", padding: 8, marginTop: 4, width: "100%" }}>
            {businesses.map((b) => (
              <option key={b.id} value={b.id}>
                {b.name}
              </option>
            ))}
          </select>
        </label>
        {loading ? (
          <p className="rc-loading">Loading...</p>
        ) : (
          <div className="rc-table-scroll"><table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
            <tbody>
              {calls.map((c) => (
                <tr
                  key={c.id}
                  onClick={() => setSelectedCallId(c.id)}
                  style={{
                    borderBottom: "1px solid var(--rc-border-soft)",
                    cursor: "pointer",
                    background: selectedCallId === c.id ? "var(--rc-primary-soft)" : undefined,
                  }}
                >
                  <td style={{ padding: "10px 12px" }}>
                    <div>{c.caller_number ? formatAuPhoneForDisplay(c.caller_number) : "Unknown"}</div>
                    <div style={{ color: "var(--rc-text-faint)" }}>
                      {c.started_at ? formatDateTime(c.started_at) : "-"} · {c.outcome ?? "-"}
                    </div>
                  </td>
                </tr>
              ))}
              {calls.length === 0 && (
                <tr>
                  <td style={{ padding: 16, color: "var(--rc-text-faint)" }}>No calls yet.</td>
                </tr>
              )}
            </tbody>
          </table></div>
        )}
      </div>

      <div style={{ flex: 1, minWidth: 0 }}>
        {!selectedCallId ? (
          <p style={{ color: "var(--rc-text-faint)" }}>Select a call to see its raw trace.</p>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: 24 }}>
            <section>
              <h2 className="rc-h2" style={{ marginBottom: 8 }}>Timeline</h2>
              <div className="rc-table-scroll"><table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
                <tbody>
                  {events.map((e) => (
                    <tr key={e.id} style={{ borderBottom: "1px solid var(--rc-border-soft)" }}>
                      <td style={{ padding: 6, color: "var(--rc-text-faint)", whiteSpace: "nowrap" }}>{formatTimeWithSeconds(e.at)}</td>
                      <td style={{ padding: 6, fontWeight: 600 }}>{e.type}</td>
                      <td style={{ padding: 6 }}>
                        <pre style={preStyle}>{JSON.stringify(e.detail, null, 2)}</pre>
                      </td>
                    </tr>
                  ))}
                  {events.length === 0 && (
                    <tr>
                      <td colSpan={3} style={{ padding: "10px 12px", color: "var(--rc-text-faint)" }}>
                        No events recorded.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table></div>
            </section>

            {raw ? (
              <>
                <RawSection title="Transcript" value={raw.transcript} />
                <RawSection title="Tool trace" value={raw.tool_trace} />
                <RawSection title="Model messages" value={raw.model_messages} />
                <RawSection title="Usage" value={raw.usage} />
                <RawSection title="Latency" value={raw.latency} />
              </>
            ) : (
              <p style={{ color: "var(--rc-text-faint)" }}>No raw data captured for this call.</p>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function RawSection({ title, value }: { title: string; value: unknown }) {
  if (value == null) return null;
  return (
    <section>
      <h2 className="rc-h2" style={{ marginBottom: 8 }}>{title}</h2>
      <pre style={preStyle}>{JSON.stringify(value, null, 2)}</pre>
    </section>
  );
}

const preStyle: React.CSSProperties = {
  background: "var(--rc-surface-sunken)",
  border: "1px solid var(--rc-border-soft)",
  borderRadius: 6,
  padding: 12,
  fontSize: 12,
  overflowX: "auto",
  margin: 0,
  maxHeight: 320,
  overflowY: "auto",
};
